/**
 * LegalIconMenu — pływająca ikona na stronach publicznych (login, formularze)
 * otwierająca menu z linkami do dokumentów prawnych.
 *
 * Design: gold-outline w rogu, tooltip po najechaniu, popup-menu po kliknięciu.
 * Umiejscowienie: fixed bottom-right, z-index niższy niż cookie-banner
 * (żeby nie kolidowało).
 */
import React, { useState, useEffect, useRef } from 'react';
import { Scale, FileText, Shield, Cookie, ClipboardList, Trophy, X } from 'lucide-react';

const LegalIconMenu = ({ position = 'bottom-right' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  // Position styles
  const positionStyle = {
    'bottom-right': { bottom: 20, right: 20 },
    'bottom-left':  { bottom: 20, left: 20 },
    'top-right':    { top: 20, right: 20 },
  }[position] || { bottom: 20, right: 20 };

  return (
    <div
      ref={ref}
      style={{ position: 'fixed', zIndex: 8500, ...positionStyle }}
      data-testid="legal-icon-menu"
    >
      {/* Popup */}
      {open && (
        <div style={{
          position: 'absolute',
          bottom: 62, right: 0,
          minWidth: 260,
          background: 'linear-gradient(180deg, rgba(15,23,42,0.98), rgba(6,11,22,0.98))',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(212,175,55,0.28)',
          borderRadius: 16, padding: 8,
          boxShadow: '0 30px 80px -20px rgba(0,0,0,0.7), 0 0 40px -20px rgba(212,175,55,0.4)',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 12px 6px', borderBottom: '1px solid rgba(255,255,255,0.06)',
            marginBottom: 4,
          }}>
            <Scale size={13} style={{ color: '#d4af37' }} />
            <span style={{
              fontSize: 10, letterSpacing: 2, color: '#d4af37',
              fontWeight: 800, textTransform: 'uppercase',
            }}>
              Dokumenty prawne
            </span>
            <button onClick={() => setOpen(false)}
              style={{
                marginLeft: 'auto', width: 22, height: 22, borderRadius: 999,
                background: 'transparent', color: '#64748b', border: 'none',
                cursor: 'pointer', display: 'grid', placeItems: 'center',
              }}
              aria-label="Zamknij">
              <X size={13} />
            </button>
          </div>

          <MenuItem href="/regulamin" Icon={FileText} label="Regulamin" testId="legal-menu-regulamin" />
          <MenuItem href="/regulamin-reklamacji" Icon={ClipboardList} label="Regulamin reklamacji" testId="legal-menu-complaints" />
          <MenuItem href="/regulamin-konkursu" Icon={Trophy} label="Regulamin konkursu" testId="legal-menu-contest" />
          <MenuItem href="/polityka-prywatnosci" Icon={Shield} label="Polityka prywatności" testId="legal-menu-privacy" />
          <MenuItem href="/polityka-cookies" Icon={Cookie} label="Polityka cookies" testId="legal-menu-cookies" />

          <div style={{
            marginTop: 8, padding: '10px 12px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            fontSize: 10, color: '#64748b', lineHeight: 1.5,
          }}>
            <div style={{ color: '#94a3b8', fontWeight: 700, marginBottom: 2 }}>
              Administrator danych
            </div>
            MR JERRY JERZY FLOREK<br />
            ul. Kościuszki 18, Nowy Sącz<br />
            NIP 734-350-41-81
          </div>
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="legal-icon-btn"
        aria-label="Dokumenty prawne"
        title="Regulamin / Polityka prywatności / Cookies"
        style={{
          width: 48, height: 48, borderRadius: 999,
          background: open
            ? 'linear-gradient(135deg,#d4af37,#b8860b)'
            : 'rgba(15,23,42,0.85)',
          border: `1.5px solid ${open ? '#d4af37' : 'rgba(212,175,55,0.4)'}`,
          color: open ? '#0b1220' : '#d4af37',
          cursor: 'pointer',
          display: 'grid', placeItems: 'center',
          boxShadow: '0 12px 30px -8px rgba(0,0,0,0.6), 0 0 24px -8px rgba(212,175,55,0.35)',
          backdropFilter: 'blur(16px)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease',
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.08)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
      >
        <Scale size={20} strokeWidth={2} />
      </button>
    </div>
  );
};

const MenuItem = ({ href, Icon, label, testId }) => (
  <a href={href} target="_blank" rel="noreferrer"
    data-testid={testId}
    style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 12px', borderRadius: 10,
      color: '#cbd5e1', textDecoration: 'none',
      fontSize: 13, fontWeight: 600,
      transition: 'background 0.15s ease, color 0.15s ease',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212,175,55,0.08)'; e.currentTarget.style.color = '#d4af37'; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#cbd5e1'; }}
  >
    <div style={{
      width: 30, height: 30, borderRadius: 8,
      background: 'rgba(212,175,55,0.10)', color: '#d4af37',
      display: 'grid', placeItems: 'center', flexShrink: 0,
    }}>
      <Icon size={14} strokeWidth={1.8} />
    </div>
    <span>{label}</span>
  </a>
);

export default LegalIconMenu;
